import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const VIZ_TAG_PREFIX = 'xqviz://';
export const SURVEY_VIZ_TAG_PREFIX = 'xqsurviz://';
export const PHOTO_TAG_PREFIX = 'xqphoto://';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface TagUrlResponse {
  url: string;
}

type ParsedImageTag =
  | { kind: 'district'; eoaId: string; visualizationName: string }
  | { kind: 'survey'; eoaId: string; schoolId: string | null; visualizationName: string }
  | { kind: 'photo'; eoaId: string; dataFileId: string };

const isImageTag = (value: string): boolean =>
  value.startsWith(VIZ_TAG_PREFIX) || value.startsWith(SURVEY_VIZ_TAG_PREFIX) || value.startsWith(PHOTO_TAG_PREFIX);

const parseImageTag = (tag: string): ParsedImageTag | null => {
  if (tag.startsWith(VIZ_TAG_PREFIX)) {
    const rest = tag.slice(VIZ_TAG_PREFIX.length);
    const slash = rest.indexOf('/');
    if (slash <= 0) return null;
    const eoaId = rest.slice(0, slash);
    const visualizationName = rest.slice(slash + 1);
    if (!eoaId || !visualizationName) return null;
    return { kind: 'district', eoaId, visualizationName };
  }
  if (tag.startsWith(SURVEY_VIZ_TAG_PREFIX)) {
    const rest = tag.slice(SURVEY_VIZ_TAG_PREFIX.length);
    const firstSlash = rest.indexOf('/');
    if (firstSlash <= 0) return null;
    const eoaId = rest.slice(0, firstSlash);
    const afterEoaId = rest.slice(firstSlash + 1);
    if (!eoaId || !afterEoaId) return null;

    const secondSlash = afterEoaId.indexOf('/');
    if (secondSlash === -1) {
      if (UUID_REGEX.test(afterEoaId)) return null;
      return { kind: 'survey', eoaId, schoolId: null, visualizationName: afterEoaId };
    }

    const potentialSchoolId = afterEoaId.slice(0, secondSlash);
    const visualizationName = afterEoaId.slice(secondSlash + 1);
    if (!potentialSchoolId) return null;
    if (UUID_REGEX.test(potentialSchoolId)) {
      if (!visualizationName) return null;
      return { kind: 'survey', eoaId, schoolId: potentialSchoolId, visualizationName };
    }

    return { kind: 'survey', eoaId, schoolId: null, visualizationName: afterEoaId };
  }
  if (tag.startsWith(PHOTO_TAG_PREFIX)) {
    const rest = tag.slice(PHOTO_TAG_PREFIX.length);
    const slash = rest.indexOf('/');
    if (slash <= 0) return null;
    const eoaId = rest.slice(0, slash);
    const dataFileId = rest.slice(slash + 1);
    if (!UUID_REGEX.test(eoaId) || !UUID_REGEX.test(dataFileId)) return null;
    return { kind: 'photo', eoaId, dataFileId };
  }
  return null;
};

const imageUrlPath = (parsed: ParsedImageTag): string => {
  if (parsed.kind === 'district') {
    return `/eoa/${parsed.eoaId}/visualizations/${parsed.visualizationName}/url`;
  }
  if (parsed.kind === 'photo') {
    return `/eoa/${parsed.eoaId}/workshop-artifacts/photos/${parsed.dataFileId}/url`;
  }
  if (parsed.schoolId === null) {
    return `/eoa/${parsed.eoaId}/survey-visualizations/${parsed.visualizationName}/url`;
  }
  return `/eoa/${parsed.eoaId}/survey-visualizations/${parsed.schoolId}/${parsed.visualizationName}/url`;
};

const resolveImageTag = async (tag: string, accessToken: string): Promise<string | null> => {
  const parsed = parseImageTag(tag);
  if (!parsed) return null;
  const response = await axios.get<TagUrlResponse>(`${BASE_URL}${imageUrlPath(parsed)}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data.url;
};

export { isImageTag, parseImageTag, resolveImageTag };
export type { ParsedImageTag };
